import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  LayoutGroup,
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
} from "framer-motion";
import {
  ArrowUpRight,
  CircleUserRound,
  Code2,
  Compass,
  Handshake,
  LayoutGrid,
  LockKeyhole,
  Search,
  ShieldCheck,
  Store,
  Wallet,
  X,
} from "lucide-react";
import { hashHref } from "../constants/site";
import {
  CATEGORY_BY_ID,
  FAQ_CATEGORIES,
  FAQ_ITEMS,
  POPULAR_FAQ_IDS,
  matchesFaqQuery,
} from "../constants/faqs";

const EASE = [0.22, 1, 0.36, 1];
const TOTAL = FAQ_ITEMS.length;
const SUGGESTION_LIMIT = 6;
const POPULAR = POPULAR_FAQ_IDS.map((id) => FAQ_ITEMS.find((item) => item.id === id)).filter(Boolean);
const STICKY_TOP = "calc(4.5rem + 3px + 0.75rem)";
const STICKY_MAX_H = "calc(100vh - 4.5rem - 3px - 1rem)";

const ICONS = {
  compass: Compass,
  store: Store,
  shield: ShieldCheck,
  wallet: Wallet,
  code: Code2,
  handshake: Handshake,
  lock: LockKeyhole,
  user: CircleUserRound,
};

function CornerFrame({ className = "" }) {
  return (
    <div className={`pointer-events-none absolute inset-0 ${className}`} aria-hidden="true">
      <span className="absolute -left-2 -top-2 h-10 w-10 border-l border-t border-gold" />
      <span className="absolute -right-2 -top-2 h-10 w-10 border-r border-t border-navy/20" />
      <span className="absolute -bottom-2 -left-2 h-10 w-10 border-b border-l border-navy/20" />
      <span className="absolute -bottom-2 -right-2 h-10 w-10 border-b border-r border-gold" />
    </div>
  );
}

function HighlightedText({ text, query }) {
  const tokens = query
    .trim()
    .split(/\s+/)
    .filter((token) => token.length >= 2);
  if (!tokens.length || !text) return text;

  const escaped = tokens.map((token) => token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
  const parts = text.split(new RegExp(`(${escaped})`, "gi"));
  const matched = new Set(tokens.map((token) => token.toLowerCase()));

  return parts.map((part, index) =>
    matched.has(part.toLowerCase()) ? (
      <mark key={`${part}-${index}`} className="bg-gold/45 text-navy">
        {part}
      </mark>
    ) : (
      part
    )
  );
}

function FaqAnswer({ item, query }) {
  return (
    <div className="space-y-3">
      <p>
        <HighlightedText text={item.answer} query={query} />
      </p>
      {item.bullets?.length ? (
        <ul className="space-y-2">
          {item.bullets.map((bullet) => (
            <li key={bullet} className="flex gap-3">
              <span className="mt-[0.55rem] h-1.5 w-1.5 shrink-0 bg-gold" aria-hidden="true" />
              <span>
                <HighlightedText text={bullet} query={query} />
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {item.after ? (
        <p>
          <HighlightedText text={item.after} query={query} />
        </p>
      ) : null}
    </div>
  );
}

function FaqLibrary() {
  const reduce = useReducedMotion();
  const mobileListboxId = useId();
  const desktopListboxId = useId();
  const mobileInputId = useId();
  const desktopInputId = useId();
  const mobileInputRef = useRef(null);
  const desktopInputRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const desktopSearchRef = useRef(null);
  const sentinelRef = useRef(null);
  const listRef = useRef(null);
  const [query, setQuery] = useState("");
  const [topic, setTopic] = useState("all");
  const [open, setOpen] = useState("");
  const [expandAll, setExpandAll] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [activeSuggest, setActiveSuggest] = useState(-1);
  const [pinned, setPinned] = useState(false);
  const [spyId, setSpyId] = useState("general");

  const fade = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    show: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0 } : { duration: 0.55, ease: EASE },
    },
  };

  const { scrollYProgress } = useScroll({
    target: listRef,
    offset: ["start start", "end end"],
  });
  const progress = useSpring(scrollYProgress, {
    stiffness: reduce ? 400 : 140,
    damping: reduce ? 40 : 28,
    mass: 0.35,
  });

  const filtered = useMemo(() => {
    return FAQ_ITEMS.filter((item) => {
      if (topic !== "all" && item.categoryId !== topic) return false;
      return matchesFaqQuery(item, query);
    });
  }, [query, topic]);

  const grouped = useMemo(() => {
    return FAQ_CATEGORIES.map((category) => ({
      category,
      items: filtered.filter((item) => item.categoryId === category.id),
    })).filter((group) => group.items.length > 0);
  }, [filtered]);

  const suggestions = useMemo(() => {
    if (query.trim().length < 2) return [];
    return FAQ_ITEMS.filter((item) => matchesFaqQuery(item, query)).slice(0, SUGGESTION_LIMIT);
  }, [query]);

  const topicCounts = useMemo(() => {
    const counts = { all: FAQ_ITEMS.filter((item) => matchesFaqQuery(item, query)).length };
    FAQ_CATEGORIES.forEach((category) => {
      counts[category.id] = FAQ_ITEMS.filter(
        (item) => item.categoryId === category.id && matchesFaqQuery(item, query)
      ).length;
    });
    return counts;
  }, [query]);

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (!hash) {
      window.scrollTo(0, 0);
      return undefined;
    }

    if (hash.startsWith("faq-")) {
      const n = hash.slice(4).padStart(2, "0");
      const item = FAQ_ITEMS.find((entry) => entry.n === n || String(entry.id) === hash.slice(4));
      if (item) {
        setTopic("all");
        setOpen(item.n);
        const timer = window.setTimeout(() => {
          document.getElementById(`faq-item-${item.n}`)?.scrollIntoView({
            behavior: reduce ? "auto" : "smooth",
            block: "start",
          });
        }, 180);
        return () => window.clearTimeout(timer);
      }
    }

    if (hash.startsWith("topic-")) {
      const id = hash.slice(6);
      if (FAQ_CATEGORIES.some((category) => category.id === id)) {
        setTopic(id);
      }
    }

    return undefined;
  }, [reduce]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || event.target?.isContentEditable) return;
      event.preventDefault();
      const desktop = window.matchMedia("(min-width: 1024px)").matches;
      (desktop ? desktopInputRef : mobileInputRef).current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const onPointer = (event) => {
      const inMobile = mobileSearchRef.current?.contains(event.target);
      const inDesktop = desktopSearchRef.current?.contains(event.target);
      if (!inMobile && !inDesktop) {
        setSuggestOpen(false);
        setActiveSuggest(-1);
      }
    };
    window.addEventListener("pointerdown", onPointer);
    return () => window.removeEventListener("pointerdown", onPointer);
  }, []);

  useEffect(() => {
    if (open && !filtered.some((item) => item.n === open)) {
      setOpen("");
    }
  }, [filtered, open]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setPinned(!entry.isIntersecting),
      { threshold: 0, rootMargin: "-76px 0px 0px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (topic !== "all") {
      setSpyId(topic);
      return undefined;
    }

    const nodes = grouped
      .map((group) => document.getElementById(`faq-group-${group.category.id}`))
      .filter(Boolean);
    if (!nodes.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const id = visible[0]?.target.getAttribute("data-topic");
        if (id) setSpyId(id);
      },
      { rootMargin: "-28% 0px -55% 0px", threshold: [0, 0.15, 0.4] }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [topic, grouped]);

  const motionOff = reduce ? { duration: 0 } : { duration: 0.35, ease: EASE };

  const jumpToItem = (item, options = {}) => {
    const { keepQuery = false, closeSuggest = true } = options;
    if (!keepQuery) setQuery("");
    setTopic("all");
    setExpandAll(false);
    setOpen(item.n);
    if (closeSuggest) {
      setSuggestOpen(false);
      setActiveSuggest(-1);
    }
    window.history.replaceState(null, "", `#faq-${item.n}`);
    window.requestAnimationFrame(() => {
      document.getElementById(`faq-button-${item.n}`)?.focus({ preventScroll: true });
      document.getElementById(`faq-item-${item.n}`)?.scrollIntoView({
        behavior: reduce ? "auto" : "smooth",
        block: "start",
      });
    });
  };

  const selectTopic = (id) => {
    setTopic(id);
    setExpandAll(false);
    setOpen("");
    window.history.replaceState(null, "", id === "all" ? "/faqs" : `#topic-${id}`);
    window.requestAnimationFrame(() => {
      document.getElementById("faq-answers")?.scrollIntoView({
        behavior: reduce ? "auto" : "smooth",
        block: "start",
      });
    });
  };

  const toggle = (n) => {
    setExpandAll(false);
    setOpen((current) => (current === n ? "" : n));
    window.history.replaceState(null, "", `#faq-${n}`);
  };

  const onSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      if (suggestOpen) {
        setSuggestOpen(false);
        setActiveSuggest(-1);
        return;
      }
      if (query) {
        setQuery("");
        return;
      }
      event.target.blur();
      return;
    }

    if (!suggestions.length) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setSuggestOpen(true);
      setActiveSuggest((current) => (current + 1) % suggestions.length);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setSuggestOpen(true);
      setActiveSuggest((current) => (current <= 0 ? suggestions.length - 1 : current - 1));
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const chosen = suggestions[activeSuggest] || suggestions[0];
      if (chosen) jumpToItem(chosen, { keepQuery: true });
    }
  };

  const clearFilters = () => {
    setQuery("");
    setTopic("all");
    setOpen("");
    setExpandAll(false);
    window.history.replaceState(null, "", "/faqs");
    const desktop = window.matchMedia("(min-width: 1024px)").matches;
    (desktop ? desktopInputRef : mobileInputRef).current?.focus();
  };

  const searchProps = {
    query,
    setQuery,
    suggestions,
    suggestOpen,
    setSuggestOpen,
    activeSuggest,
    setActiveSuggest,
    onSearchKeyDown,
    jumpToItem,
  };

  const activeCategory = topic === "all" ? null : CATEGORY_BY_ID[topic];
  const showingSuggestions = suggestOpen && suggestions.length > 0;
  const highlightedTopic = topic === "all" ? spyId : topic;

  const sidebar = (
    <TopicSidebar
      pinned={pinned}
      progress={progress}
      reduce={reduce}
      query={query}
      topic={topic}
      highlightedTopic={highlightedTopic}
      topicCounts={topicCounts}
      selectTopic={selectTopic}
      jumpToItem={jumpToItem}
      search={
        <SearchField
          {...searchProps}
          inputId={desktopInputId}
          listboxId={desktopListboxId}
          inputRef={desktopInputRef}
          wrapRef={desktopSearchRef}
          showingSuggestions={showingSuggestions}
        />
      }
    />
  );

  return (
    <div className="relative bg-white text-navy">
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_0%_0%,rgba(255,191,24,0.12),transparent_55%),radial-gradient(50%_40%_at_100%_12%,rgba(79,72,236,0.08),transparent_50%)]" />
        <div
          className="absolute inset-0 opacity-[0.3]"
          style={{
            backgroundImage: "radial-gradient(rgba(16,14,52,0.08) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
        <p className="absolute -left-4 top-16 select-none whitespace-nowrap text-[7rem] font-black leading-none tracking-tight text-navy/[0.035] sm:text-[10rem] lg:top-10 lg:text-[13rem]">
          FAQS
        </p>
      </div>

      <section className="relative" aria-labelledby="faqs-page-heading">
        <div className="relative mx-auto max-w-8xl px-4 pb-8 pt-28 sm:px-6 lg:px-8 lg:pb-10 lg:pt-32">
          <motion.div initial="hidden" animate="show" variants={fade}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Help centre
            </p>
            <h1
              id="faqs-page-heading"
              className="mt-4 max-w-4xl font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Frequently asked questions
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/55 sm:text-lg">
              Search the SimFinance library, pin a topic in the rail, or browse all {TOTAL} answers
              across the marketplace, verification, collections, APIs, and partnerships.
            </p>

            <dl className="mt-8 grid max-w-xl grid-cols-3 gap-px border border-navy/10 bg-navy/10">
              {[
                { label: "Answers", value: String(TOTAL).padStart(2, "0") },
                { label: "Topics", value: "08" },
                { label: "Search", value: "Live" },
              ].map((stat) => (
                <div key={stat.label} className="bg-white px-4 py-4 sm:px-5">
                  <dt className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-navy/35">
                    {stat.label}
                  </dt>
                  <dd className="mt-1 text-2xl font-semibold tracking-tight text-navy">{stat.value}</dd>
                </div>
              ))}
            </dl>
          </motion.div>
        </div>
      </section>

      <div className="sticky top-[calc(4.5rem+3px)] z-30 border-y border-navy/10 bg-white/92 backdrop-blur-md lg:hidden">
        <div className="mx-auto max-w-8xl px-4 py-3 sm:px-6">
          <SearchField
            {...searchProps}
            inputId={mobileInputId}
            listboxId={mobileListboxId}
            inputRef={mobileInputRef}
            wrapRef={mobileSearchRef}
            showingSuggestions={showingSuggestions}
            compact
          />
          <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Filter by topic">
            <FilterChip
              pressed={topic === "all"}
              onClick={() => selectTopic("all")}
              label="All"
              count={topicCounts.all}
            />
            {FAQ_CATEGORIES.map((category) => (
              <FilterChip
                key={category.id}
                pressed={topic === category.id}
                onClick={() => selectTopic(category.id)}
                label={category.label}
                count={topicCounts[category.id]}
                disabled={topicCounts[category.id] === 0 && topic !== category.id}
              />
            ))}
          </div>
        </div>
      </div>

      <section
        id="faq-library"
        className="relative scroll-mt-[14rem] lg:scroll-mt-28"
        aria-labelledby="faq-library-heading"
      >
        <div ref={sentinelRef} className="h-px w-full" aria-hidden="true" />
        <div className="relative mx-auto max-w-8xl px-4 pb-8 sm:px-6 lg:px-8 lg:pb-10 lg:pt-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
            Popular questions
          </p>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            {POPULAR.map((item) => (
              <li key={item.n}>
                <button
                  type="button"
                  onClick={() => jumpToItem(item)}
                  className="flex min-h-16 w-full cursor-pointer items-start gap-3 border border-navy/10 bg-white px-4 py-3 text-left transition-colors hover:border-gold hover:bg-[#fffaf0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal"
                >
                  <span className="mt-0.5 font-mono text-[11px] font-semibold tracking-[0.16em] text-gold">
                    {item.n}
                  </span>
                  <span className="text-sm font-semibold leading-snug text-navy">{item.question}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative mx-auto grid max-w-8xl items-start gap-8 px-4 pb-20 sm:px-6 lg:grid-cols-[minmax(19rem,22rem)_minmax(0,1fr)] lg:gap-12 lg:px-8 lg:pb-28">
          <aside
            className="hidden lg:sticky lg:z-20 lg:flex lg:self-start"
            style={{ top: STICKY_TOP, height: STICKY_MAX_H, maxHeight: STICKY_MAX_H }}
            aria-label="Browse by topic"
          >
            {sidebar}
          </aside>

          <div
            ref={listRef}
            id="faq-answers"
            className="relative min-w-0 scroll-mt-[14rem] lg:scroll-mt-28"
          >
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2
                  id="faq-library-heading"
                  className="text-2xl font-semibold tracking-tight text-navy sm:text-[1.75rem]"
                >
                  {activeCategory ? activeCategory.name : "Full library"}
                </h2>
                <p className="mt-1 text-sm text-navy/55" aria-live="polite">
                  Showing{" "}
                  <span className="font-semibold text-navy">
                    {filtered.length} {filtered.length === 1 ? "answer" : "answers"}
                  </span>
                  {query.trim() ? <> for “{query.trim()}”</> : null}
                  {activeCategory ? ` in ${activeCategory.label}` : ""}.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setExpandAll(true);
                    setOpen("");
                  }}
                  className="inline-flex min-h-11 cursor-pointer items-center border border-navy/15 bg-white px-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/70 transition hover:border-navy/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal"
                >
                  Expand all
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setExpandAll(false);
                    setOpen("");
                  }}
                  disabled={!expandAll && !open}
                  className="inline-flex min-h-11 cursor-pointer items-center border border-navy/15 bg-white px-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/70 transition hover:border-navy/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Collapse all
                </button>
              </div>
            </div>

            {filtered.length === 0 ? (
              <EmptyState query={query} onReset={clearFilters} onJump={jumpToItem} />
            ) : (
              <div className="relative">
                <CornerFrame />
                <div className="relative overflow-hidden border border-navy/10 bg-white">
                  {grouped.map((group, groupIndex) => (
                    <div
                      key={group.category.id}
                      id={`faq-group-${group.category.id}`}
                      data-topic={group.category.id}
                      className={groupIndex === 0 ? "" : "border-t border-navy/10"}
                    >
                      <p className="border-b border-navy/10 bg-[#f7f6fb] px-5 py-3 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-navy/40 sm:px-6">
                        {group.category.letter}. {group.category.label} — {group.category.name}
                      </p>
                      {group.items.map((item) => {
                        const isOpen = expandAll || open === item.n;
                        const panelId = `faq-panel-${item.n}`;
                        const buttonId = `faq-button-${item.n}`;

                        return (
                          <div
                            key={item.n}
                            id={`faq-item-${item.n}`}
                            className="scroll-mt-[14rem] border-b border-navy/10 last:border-b-0 lg:scroll-mt-28"
                          >
                            <h3>
                              <button
                                type="button"
                                id={buttonId}
                                aria-expanded={isOpen}
                                aria-controls={panelId}
                                onClick={() => toggle(item.n)}
                                className={`flex w-full cursor-pointer items-start gap-4 px-5 py-5 text-left transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-royal sm:gap-5 sm:px-6 ${
                                  isOpen ? "bg-[#f7f6fb]" : "bg-white hover:bg-[#f7f6fb]"
                                }`}
                              >
                                <span
                                  className={`relative mt-1 block h-8 w-[2px] shrink-0 ${
                                    isOpen ? "bg-gold" : "bg-navy/10"
                                  }`}
                                  aria-hidden="true"
                                />
                                <span
                                  className={`mt-0.5 shrink-0 font-mono text-[11px] font-semibold tracking-[0.16em] ${
                                    isOpen ? "text-gold" : "text-navy/30"
                                  }`}
                                >
                                  {item.n}
                                </span>
                                <span className="min-w-0 flex-1 text-base font-semibold leading-snug tracking-tight text-navy sm:text-lg">
                                  <HighlightedText text={item.question} query={query} />
                                </span>
                                <span
                                  aria-hidden="true"
                                  className={`relative mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center border ${
                                    isOpen
                                      ? "border-gold bg-gold text-navy"
                                      : "border-navy/15 bg-white text-navy"
                                  }`}
                                >
                                  <span className="absolute h-px w-3 bg-current" />
                                  <span
                                    className={`absolute h-3 w-px bg-current transition-transform duration-200 ${
                                      isOpen ? "scale-y-0" : "scale-y-100"
                                    }`}
                                  />
                                </span>
                              </button>
                            </h3>
                            <motion.div
                              id={panelId}
                              role="region"
                              aria-labelledby={buttonId}
                              initial={false}
                              animate={isOpen ? { height: "auto", opacity: 1 } : { height: 0, opacity: 0 }}
                              transition={motionOff}
                              className="overflow-hidden"
                            >
                              <div
                                className={`px-5 pb-6 pl-[4.75rem] text-sm leading-relaxed text-navy/60 sm:px-6 sm:pl-[6.25rem] sm:text-base ${
                                  isOpen ? "bg-[#f7f6fb]" : ""
                                }`}
                              >
                                <FaqAnswer item={item} query={query} />
                              </div>
                            </motion.div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="relative bg-[#f7f6fb]" aria-labelledby="faq-support-heading">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_0%_0%,rgba(255,191,24,0.16),transparent_55%),radial-gradient(55%_45%_at_100%_100%,rgba(79,72,236,0.08),transparent_50%)]" />
        </div>
        <div className="relative mx-auto grid max-w-8xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-16 lg:px-8 lg:py-24">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Still looking?
            </p>
            <h2
              id="faq-support-heading"
              className="mt-3 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(1.75rem,4vw,3rem)]"
            >
              Can’t find the answer you need?
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-navy/55 sm:text-lg">
              Talk to the SimFinance team about a demonstration, partnership, or a specific product
              question. We will route you to the right specialist.
            </p>
          </div>

          <div className="relative">
            <CornerFrame />
            <div className="relative overflow-hidden border border-navy/10 bg-white">
              <div className="h-[3px] bg-gradient-to-r from-gold via-royal to-gold" />
              <div className="grid gap-px bg-navy/10">
                {[
                  {
                    n: "01",
                    label: "Request a Demo",
                    hint: "See the platform with a specialist",
                    href: hashHref("contact"),
                    tone: "gold",
                  },
                  {
                    n: "02",
                    label: "Become a Partner",
                    hint: "Join the ecosystem and grow",
                    href: hashHref("partner"),
                    tone: "navy",
                  },
                  {
                    n: "03",
                    label: "Contact Support",
                    hint: "Account, technical, and general enquiries",
                    href: hashHref("callback"),
                    tone: "light",
                  },
                ].map((action) => (
                  <a
                    key={action.label}
                    href={action.href}
                    className={`group flex min-h-[4.75rem] cursor-pointer items-center justify-between gap-4 px-5 py-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-royal sm:px-6 ${
                      action.tone === "gold"
                        ? "bg-gold text-navy hover:bg-[#ffd056]"
                        : action.tone === "navy"
                          ? "bg-navy text-white hover:bg-[#1a173f]"
                          : "bg-white text-navy hover:bg-[#f7f6fb]"
                    }`}
                  >
                    <span className="flex min-w-0 items-start gap-4">
                      <span
                        className={`mt-1 shrink-0 font-mono text-[11px] font-semibold tracking-[0.2em] ${
                          action.tone === "gold"
                            ? "text-navy/40"
                            : action.tone === "navy"
                              ? "text-gold"
                              : "text-navy/30"
                        }`}
                      >
                        {action.n}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-base font-semibold tracking-tight sm:text-lg">
                          {action.label}
                        </span>
                        <span
                          className={`mt-1 block text-sm ${
                            action.tone === "navy" ? "text-white/55" : "text-navy/50"
                          }`}
                        >
                          {action.hint}
                        </span>
                      </span>
                    </span>
                    <ArrowUpRight
                      className={`h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${
                        action.tone === "navy" ? "text-gold" : "text-navy"
                      }`}
                      aria-hidden="true"
                    />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function TopicSidebar({
  pinned,
  progress,
  reduce,
  query,
  topic,
  highlightedTopic,
  topicCounts,
  selectTopic,
  jumpToItem,
  search,
}) {
  return (
    <div
      className={`relative flex h-full min-h-0 min-w-0 flex-1 flex-col border bg-white transition-[box-shadow,border-color] duration-300 ${
        pinned ? "border-gold/80 shadow-[0_28px_64px_-28px_rgba(16,14,52,0.38)]" : "border-navy/10"
      }`}
    >
      <CornerFrame />
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div className="relative h-[3px] bg-navy/10">
          <motion.div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-gold via-royal to-gold"
            style={{ scaleX: progress, transformOrigin: "0% 50%" }}
          />
        </div>

        <div className="border-b border-navy/10 px-5 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
            Browse by topic
          </p>
          <p className="mt-1 text-sm font-semibold tracking-tight text-navy">
            {topic === "all" ? "Eight libraries. One ecosystem." : CATEGORY_BY_ID[topic]?.name}
          </p>
        </div>

        <div className="relative z-20 shrink-0 border-b border-navy/10 px-4 py-4">{search}</div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3 [scrollbar-color:rgba(16,14,52,0.2)_transparent] [scrollbar-width:thin]">
          <div className="mb-4 border-b border-navy/10 px-2 pb-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-navy/40">
              Popular
            </p>
            <ul className="mt-2 space-y-1">
              {POPULAR.map((item) => (
                <li key={item.n}>
                  <button
                    type="button"
                    onClick={() => jumpToItem(item)}
                    className="flex w-full cursor-pointer items-start gap-2.5 px-2 py-2 text-left transition-colors hover:bg-[#f7f6fb] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal"
                  >
                    <span className="mt-0.5 font-mono text-[10px] font-semibold tracking-[0.16em] text-gold">
                      {item.n}
                    </span>
                    <span className="text-[13px] font-semibold leading-snug text-navy">
                      {item.question}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <LayoutGroup id="faq-topic-rail">
            <nav aria-label="Browse FAQ topics">
              <ul className="space-y-1.5">
                <li>
                  <TopicRailButton
                    active={topic === "all"}
                    scanning={false}
                    onClick={() => selectTopic("all")}
                    letter="00"
                    range={`${String(topicCounts.all).padStart(2, "0")} answers`}
                    label="All topics"
                    description="The complete SimFinance library"
                    icon={LayoutGrid}
                    count={topicCounts.all}
                    reduce={reduce}
                  />
                </li>
                {FAQ_CATEGORIES.map((category) => {
                  const Icon = ICONS[category.icon];
                  const active = topic === category.id;
                  const scanning = topic === "all" && highlightedTopic === category.id;
                  return (
                    <li key={category.id}>
                      <TopicRailButton
                        active={active}
                        scanning={scanning}
                        onClick={() => selectTopic(category.id)}
                        disabled={topicCounts[category.id] === 0 && !active}
                        letter={category.letter}
                        range={category.range}
                        label={category.label}
                        description={category.description}
                        icon={Icon}
                        count={topicCounts[category.id]}
                        reduce={reduce}
                      />
                    </li>
                  );
                })}
              </ul>
            </nav>
          </LayoutGroup>
        </div>

        {query.trim() ? (
          <p className="border-t border-navy/10 px-5 py-3 text-[11px] leading-relaxed text-navy/45">
            Filtering live for “{query.trim()}”. Counts update as you type.
          </p>
        ) : null}
      </div>
    </div>
  );
}

function TopicRailButton({
  active,
  scanning,
  onClick,
  disabled = false,
  letter,
  range,
  label,
  description,
  icon: Icon,
  count,
  reduce,
}) {
  const selected = active || scanning;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      disabled={disabled}
      className={`relative flex w-full cursor-pointer items-start gap-3 overflow-hidden px-3 py-3 text-left transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-royal disabled:cursor-not-allowed disabled:opacity-40 ${
        active ? "text-navy" : selected ? "text-navy" : "text-navy/70 hover:bg-[#f7f6fb]"
      }`}
    >
      {active ? (
        <motion.span
          layoutId="faq-topic-active"
          className="absolute inset-0 bg-[#fffaf0]"
          transition={reduce ? { duration: 0 } : { duration: 0.28, ease: EASE }}
          aria-hidden="true"
        />
      ) : scanning ? (
        <span className="absolute inset-0 bg-[#f7f6fb]" aria-hidden="true" />
      ) : null}
      <span
        className={`relative mt-0.5 h-10 w-[2px] shrink-0 ${active ? "bg-gold" : selected ? "bg-royal/50" : "bg-navy/10"}`}
        aria-hidden="true"
      />
      <span className="relative min-w-0 flex-1">
        <span className="flex items-center justify-between gap-2">
          <span className="font-mono text-[10px] font-semibold tracking-[0.2em] text-navy/35">
            {letter} · {range}
          </span>
          <Icon
            className={`h-4 w-4 shrink-0 ${active ? "text-gold" : "text-royal/70"}`}
            strokeWidth={1.8}
            aria-hidden="true"
          />
        </span>
        <span className="mt-1 block text-sm font-semibold tracking-tight text-navy">{label}</span>
        <span className="mt-0.5 block text-xs leading-relaxed text-navy/50">{description}</span>
        <span className="mt-2 font-mono text-[10px] font-semibold tracking-[0.16em] text-navy/35">
          {String(count).padStart(2, "0")} answers
        </span>
      </span>
    </button>
  );
}

function SearchField({
  inputId,
  listboxId,
  inputRef,
  wrapRef,
  query,
  setQuery,
  suggestions,
  suggestOpen,
  setSuggestOpen,
  activeSuggest,
  setActiveSuggest,
  showingSuggestions,
  onSearchKeyDown,
  jumpToItem,
  compact = false,
}) {
  return (
    <div ref={wrapRef} className="relative">
      <label htmlFor={inputId} className="mb-2 block text-sm font-semibold text-navy">
        Search questions
      </label>
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-navy/35"
          strokeWidth={2}
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          id={inputId}
          type="search"
          role="combobox"
          name="faq-search"
          autoComplete="off"
          spellCheck="false"
          enterKeyHint="search"
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-expanded={showingSuggestions}
          aria-controls={listboxId}
          aria-activedescendant={
            activeSuggest >= 0 && suggestions[activeSuggest]
              ? `${listboxId}-opt-${suggestions[activeSuggest].n}`
              : undefined
          }
          placeholder="Trust Score, API, password…"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setSuggestOpen(true);
            setActiveSuggest(-1);
          }}
          onFocus={() => {
            if (query.trim().length >= 2) setSuggestOpen(true);
          }}
          onKeyDown={onSearchKeyDown}
          className={`w-full cursor-text border border-navy/15 bg-[#f7f6fb] pl-10 pr-12 text-sm text-navy placeholder:text-navy/35 transition focus:border-royal focus:bg-white focus:outline-none focus:ring-2 focus:ring-royal [&::-webkit-search-cancel-button]:hidden ${
            compact ? "h-12" : "h-[3.25rem]"
          }`}
        />
        <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center">
          {query ? (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setSuggestOpen(false);
                inputRef.current?.focus();
              }}
              className="inline-flex h-8 w-8 cursor-pointer items-center justify-center border border-navy/10 bg-white text-navy transition hover:border-navy/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
            </button>
          ) : (
            <kbd className="hidden border border-navy/10 bg-white px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wider text-navy/40 lg:inline">
              /
            </kbd>
          )}
        </div>
      </div>

      {showingSuggestions ? (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-40 mt-2 max-h-72 w-full overflow-auto border border-navy/10 bg-white shadow-[0_24px_60px_-28px_rgba(16,14,52,0.45)]"
        >
          {suggestions.map((item, index) => {
            const category = CATEGORY_BY_ID[item.categoryId];
            const active = index === activeSuggest;
            return (
              <li key={item.n} role="presentation">
                <button
                  type="button"
                  role="option"
                  id={`${listboxId}-opt-${item.n}`}
                  aria-selected={active}
                  onMouseEnter={() => setActiveSuggest(index)}
                  onClick={() => jumpToItem(item, { keepQuery: true })}
                  className={`flex w-full cursor-pointer items-start gap-3 px-3 py-2.5 text-left transition-colors focus-visible:outline-none ${
                    active ? "bg-[#f7f6fb]" : "bg-white hover:bg-[#f7f6fb]"
                  }`}
                >
                  <span className="mt-0.5 font-mono text-[11px] font-semibold tracking-[0.16em] text-gold">
                    {item.n}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold leading-snug text-navy">
                      <HighlightedText text={item.question} query={query} />
                    </span>
                    <span className="mt-1 block text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/40">
                      {category?.label}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

function FilterChip({ pressed, onClick, label, count, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={pressed}
      disabled={disabled}
      className={`inline-flex min-h-11 items-center gap-2 border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal ${
        pressed
          ? "cursor-pointer border-gold bg-gold text-navy"
          : disabled
            ? "cursor-not-allowed border-navy/10 bg-[#f7f6fb] text-navy/30"
            : "cursor-pointer border-navy/10 bg-[#f7f6fb] text-navy/70 hover:border-gold hover:bg-white"
      }`}
    >
      {label}
      <span className={`font-mono tracking-[0.08em] ${pressed ? "text-navy/50" : "text-navy/35"}`}>
        {count}
      </span>
    </button>
  );
}

function EmptyState({ query, onReset, onJump }) {
  return (
    <div className="relative border border-navy/10 bg-white px-6 py-12 sm:px-10">
      <CornerFrame />
      <div className="relative">
        <p className="font-mono text-[11px] font-semibold tracking-[0.22em] text-navy/35">NO MATCHES</p>
        <h3 className="mt-3 text-2xl font-semibold tracking-tight text-navy">
          No answers match{query.trim() ? ` “${query.trim()}”` : " those filters"}
        </h3>
        <p className="mt-3 max-w-lg text-sm leading-relaxed text-navy/55 sm:text-base">
          Try a shorter phrase, switch topics, or jump to one of these frequently viewed questions.
        </p>
        <ul className="mt-6 grid gap-2 sm:grid-cols-2">
          {POPULAR.slice(0, 4).map((item) => (
            <li key={item.n}>
              <button
                type="button"
                onClick={() => onJump(item)}
                className="flex w-full cursor-pointer items-start gap-3 border border-navy/10 bg-[#f7f6fb] px-4 py-3 text-left text-sm font-semibold text-navy transition hover:border-gold hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal"
              >
                <span className="font-mono text-[11px] tracking-[0.16em] text-gold">{item.n}</span>
                {item.question}
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={onReset}
          className="mt-6 inline-flex min-h-11 cursor-pointer items-center rounded-full bg-gold px-5 text-sm font-semibold text-navy transition hover:bg-[#ffd056] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal focus-visible:ring-offset-2"
        >
          Clear search and filters
        </button>
      </div>
    </div>
  );
}

export default FaqLibrary;
